import { useState } from "react";
import { useTranslation } from "react-i18next";

import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import RequirementBadge from "@/features/certifications/module/components/RequirementBadge";
import VideoCard from "@/features/certifications/module/components/VideoCard";
import VideoPlayerModal from "@/features/certifications/module/components/VideoPlayerModal";
import {
  VideoSelection,
  VideosCardProps,
} from "@/features/certifications/module/components/types";

import "./styles.css";

const BAR_STROKE_WIDTH = 4;

const VideosCard = ({
  certificationSlug,
  weekPosition,
  videos,
  itemProgress,
  isRequired,
  itemSatisfaction,
}: VideosCardProps) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<VideoSelection | null>(null);
  const progressById = new Map(itemProgress.map((item) => [item.id, item]));
  const isWatched = (videoId: number) =>
    itemSatisfaction[videoId] ??
    Boolean(progressById.get(videoId)?.video?.isComplete);
  const watchedCount = videos.filter((video) => isWatched(video.id)).length;

  return (
    <section className="certification-card module-card flex flex-col gap-300">
      <header className="module-card-header flex flex-col gap-200">
        <div className="flex items-center justify-between gap-300">
          <h2 className="certification-heading header-s-medium">
            {t("certification.module.videos.heading")}
          </h2>

          <RequirementBadge isRequired={isRequired} />
        </div>

        <BaseProgressBar
          value={watchedCount}
          total={videos.length}
          strokeWidth={BAR_STROKE_WIDTH}
          className="module-progress-bar full-width"
          label={
            <span className="certification-muted body-s-regular">
              {t("certification.module.videos.watchedCount", {
                current: watchedCount,
                target: videos.length,
              })}
            </span>
          }
        />
      </header>

      <div className="module-card-body flex flex-wrap gap-400">
        {videos.map((video, index) => (
          <VideoCard
            key={video.id}
            number={`${weekPosition}.${index + 1}`}
            video={video}
            progress={progressById.get(video.id)}
            isWatched={isWatched(video.id)}
            onPlay={setSelection}
          />
        ))}
      </div>

      <VideoPlayerModal
        certificationSlug={certificationSlug}
        selection={selection}
        onClose={() => setSelection(null)}
      />
    </section>
  );
};

export default VideosCard;
